import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import PcaConsolidadoModal from './components/modals/PcaConsolidadoModal';
import PcaConsolidationPickerModal from './components/modals/PcaConsolidationPickerModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import PcaItensConsolidadosModal from './components/modalServices/PcaItensConsolidadosModal';

function PcaConsolidado() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showItensModal, setShowItensModal] = useState(false);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const [paginationProps, setPaginationProps] = useState({ startIndex: 0, rowsPerPage: 10 });

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const cnpj = localStorage.getItem('cnpj');
        const anoBase = localStorage.getItem('anoBase');
        const res = await axios.get(`${API_BASE_URL.PCA}/consolidado`, {
          params: { cnpj, anoBase },
          headers: { Authorization: `Bearer ${token}` },
        });
        setItems(res.data || []);
      } catch (err) {
        console.error('Erro ao carregar consolidados', err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
    const handler = e => { fetchItems(); };
    window.addEventListener('anoBaseChanged', handler);
    return () => window.removeEventListener('anoBaseChanged', handler);
  }, []);

  const handleCreate = async () => {
    // create minimal entry (only ID) via API and select it
    try {
      const token = localStorage.getItem('token');
      const cnpj = localStorage.getItem('cnpj');
      const anoBase = Number(localStorage.getItem('anoBase'));
      const res = await axios.post(`${API_BASE_URL.PCA}/consolidado`, {}, { params: { cnpj, anoBase }, headers: { Authorization: `Bearer ${token}` }, suppressAuthRedirect: true });
      const created = res.data;
      // refresh list
      const r2 = await axios.get(`${API_BASE_URL.PCA}/consolidado`, { params: { cnpj, anoBase }, headers: { Authorization: `Bearer ${token}` } });
      setItems(r2.data || []);
      setSelectedId(created.id || null);
      // Não abrir o modal automaticamente ao criar; apenas notificar e selecionar
      showDefaultModal('success', 'Registro criado. Selecione e clique em Editar para alterar.');
    } catch (err) {
      console.error('Erro ao criar consolidação', err);
      showDefaultModal('error', 'Erro ao criar registro. Veja o console para detalhes.');
    }
  };

  const handleEdit = item => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = async id => {
    try {
      const token = localStorage.getItem('token');
      const cnpj = localStorage.getItem('cnpj');
      const anoBase = Number(localStorage.getItem('anoBase'));
      await axios.delete(`${API_BASE_URL.PCA}/consolidado/${id}`, { params: { cnpj, anoBase }, headers: { Authorization: `Bearer ${token}` }, suppressAuthRedirect: true });
      setItems(prev => prev.filter(i => i.id !== id));
      if (selectedId === id) setSelectedId(null);
      showDefaultModal('success', 'Registro excluído com sucesso.');
    } catch (err) {
      console.error('Erro ao excluir', err);
      showDefaultModal('error', 'Erro ao excluir registro.');
    }
  };

  const requestDelete = id => {
    if (!id) {
      showDefaultModal('error', 'Selecione um registro para excluir.');
      return;
    }
    showConfirmModal('delete', () => handleDelete(id));
  };

  const handleSave = async data => {
    try {
      const token = localStorage.getItem('token');
      if (data.id) {
        const cnpj = localStorage.getItem('cnpj');
        const anoBase = Number(localStorage.getItem('anoBase'));
        const body = {
          ...data,
          listaDfds: Array.isArray(data.listaDfds) ? data.listaDfds.join(',') : data.listaDfds,
          listaItens: Array.isArray(data.listaItens) ? data.listaItens.join(',') : data.listaItens,
          cnpj,
          anoPca: anoBase,
        };
        await axios.put(`${API_BASE_URL.PCA}/consolidado/${data.id}`, body, { headers: { Authorization: `Bearer ${token}` }, suppressAuthRedirect: true });
        setItems(prev => prev.map(i => (i.id === data.id ? data : i)));
      } else {
        const cnpj = localStorage.getItem('cnpj');
        const anoBase = Number(localStorage.getItem('anoBase'));
        const payload = {
          ...data,
          listaDfds: Array.isArray(data.listaDfds) ? data.listaDfds.join(',') : data.listaDfds,
          listaItens: Array.isArray(data.listaItens) ? data.listaItens.join(',') : data.listaItens,
        };
        const res = await axios.post(`${API_BASE_URL.PCA}/consolidado`, payload, { params: { cnpj, anoBase }, headers: { Authorization: `Bearer ${token}` }, suppressAuthRedirect: true });
        setItems(prev => [ ...(prev || []), res.data ]);
      }
      setShowModal(false);
    } catch (err) {
      console.error('Erro ao salvar consolidação', err);
      const status = err?.response?.status;
      const text = err?.response?.data || err.message;
      showDefaultModal('error', `Erro ao salvar consolidação (${status}): ${text}`);
    }
  };

  const displayed = (items || []).slice(paginationProps.startIndex, paginationProps.startIndex + paginationProps.rowsPerPage);

  const exportDisplayedToCsv = () => {
    if (!displayed || !displayed.length) return;
    const headers = ['ID', 'LISTA_DFDS_CONSOLIDADOS', 'LISTA_ITENS_CONSOLIDADOS'];
    const rows = displayed.map(r => [r.id ?? '', renderListField(r.listaDfds), renderListField(r.listaItens)]);
    const csv = [headers, ...rows].map(row => row.map(cell => {
      if (cell === null || cell === undefined) return '';
      const s = String(cell).replace(/"/g, '""');
      return `"${s}"`;
    }).join(';')).join('\r\n');

    // prepend UTF-8 BOM so Excel preserves accents/characters
    const bom = '\uFEFF';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pca_consolidado_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const totalItems = (items || []).length;

  const handleRowsPerPageChange = (e) => {
    const val = Number(e.target.value);
    setPaginationProps({ startIndex: 0, rowsPerPage: val });
  };

  const handleFirstItems = () => setPaginationProps(prev => ({ ...prev, startIndex: 0 }));
  const handlePreviousItems = () => setPaginationProps(prev => ({ ...prev, startIndex: Math.max(0, prev.startIndex - prev.rowsPerPage) }));
  const handleNextItems = () => setPaginationProps(prev => ({ ...prev, startIndex: Math.min(totalItems - prev.rowsPerPage, prev.startIndex + prev.rowsPerPage) }));
  const handleLastItems = () => setPaginationProps(prev => ({ ...prev, startIndex: Math.max(0, Math.floor((totalItems - 1) / prev.rowsPerPage) * prev.rowsPerPage) }));

  const renderListField = (field) => {
    if (field === null || field === undefined) return 'NÃO HÁ PCA CONSOLIDADO';
    if (Array.isArray(field)) return field.length ? field.join(', ') : 'NÃO HÁ PCA CONSOLIDADO';
    const s = String(field).trim();
    return s ? s : 'NÃO HÁ PCA CONSOLIDADO';
  };

  const openConsolidationPicker = () => {
    if (!selectedId) {
      showDefaultModal('error', 'Selecione um registro de Consolidação antes de consolidar.');
      return;
    }
    setShowPicker(true);
  };

  const openItens = () => {
    if (!selectedId) {
      showDefaultModal('error', 'Selecione um registro de Consolidação antes.');
      return;
    }
    setShowItensModal(true);
  };

  const handlePerformConsolidation = async selectedPcaItems => {
    // selectedPcaItems: array of PCA item objects
    // build list of item codes
    const listaItens = Array.from(new Set(selectedPcaItems.map(i => i.codigo || i.codigoItem || i.codItem || i.item).filter(Boolean)));
    // build unique list of DFD sequences and sort ascending
    const parseSeq = (dfd) => {
      if (dfd === null || dfd === undefined || dfd === '') return 0;
      const s = String(dfd);
      if (s.includes('/')) {
        const parts = s.split('/');
        const left = parts[0].replace(/\D/g, '');
        return Number(left) || 0;
      }
      const num = Number(s.replace(/\D/g, '')) || 0;
      return num % 1000;
    };
    const seqMap = new Map();
    selectedPcaItems.forEach(i => {
      const seq = parseSeq(i.dfdNum);
      if (!seqMap.has(seq)) seqMap.set(seq, i.dfdNum);
    });
    const listaDfds = Array.from(seqMap.values());
    try {
      const token = localStorage.getItem('token');
      const cnpj = localStorage.getItem('cnpj');
      const anoBase = Number(localStorage.getItem('anoBase'));
      const formatDfd = (dfd) => {
        if (dfd === null || dfd === undefined || dfd === '') return '';
        const s = String(dfd);
        if (s.includes('/')) return s;
        const num = Number(s.replace(/\D/g, '')) || 0;
        const seq = num % 1000;
        return `${String(seq).padStart(3, '0')}/${String(anoBase)}`;
      };
      // sort by sequence number ascending
      listaDfds.sort((a, b) => parseSeq(a) - parseSeq(b));
      // for each sequence, find first matching item to get requisitante and format pair
      listaDfds.sort((a, b) => parseSeq(a) - parseSeq(b));
      const listaDfdsFormatted = listaDfds.map(d => formatDfd(d));
      const listaDfdsWithReq = listaDfds
        .map(seqVal => {
          // find first selected item matching this seq
          const match = selectedPcaItems.find(it => parseSeq(it.dfdNum) === parseSeq(seqVal));
          const req = match ? (match.secretariaDepartamento || match.requisitante || '') : '';
          return `${formatDfd(seqVal)}${req ? ' - ' + req : ''}`;
        });
      const body = {
        listaDfds: listaDfdsWithReq.join(', '),
        listaItens: listaItens.join(', '),
        cnpj,
        anoPca: anoBase,
      };
      await axios.put(`${API_BASE_URL.PCA}/consolidado/${selectedId}`, body, { headers: { Authorization: `Bearer ${token}` }, suppressAuthRedirect: true });
      // Persistir itens consolidados associados ao header
      try {
        await axios.post(`${API_BASE_URL.PCA}/consolidado/${selectedId}/itens`, selectedPcaItems, { params: { cnpj, anoBase }, headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        console.error('Erro ao salvar itens consolidados:', err);
      }
      // fechar picker e abrir modal de itens consolidados
      setShowPicker(false);
      setShowItensModal(true);
      return;
    } catch (err) {
      console.error('Erro ao consolidar', err);
      showDefaultModal('error', 'Erro ao consolidar. Veja o console.');
    }
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="PCA Consolidado" icon="bi-collection" backButton={false} />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            selectedId={selectedId}
            items={items}
            onCreate={handleCreate}
            onEdit={(id) => { if (id) handleEdit(items.find(i => i.id === id)); }}
            onDelete={requestDelete}
            actions={[
              { key: 'consolidar', label: 'Consolidar', icon: 'bi-link-45deg', variant: 'success', onClick: () => openConsolidationPicker(), requiresSelection: true },
                { key: 'itens', label: 'Itens', icon: 'bi-list', variant: 'info text-white', onClick: () => openItens(), requiresSelection: true }
            ]}
          />
      <TableContainer tableClass="table-custom-radius">
        <TableHeader columns={[
          { label: 'ID', width: '6%' },
          { label: 'LISTA DE DFDS CONSOLIDADOS' },
          { label: 'LISTA DE ITENS CONSOLIDADOS' },
        ]} />
        <tbody>
          {loading ? (
            <tr><td colSpan="4" className="text-center">Carregando...</td></tr>
          ) : displayed.length ? (
            displayed.map(item => (
              <tr key={item.id || Math.random()} className={selectedId === item.id ? 'table-primary' : ''} onClick={() => setSelectedId(item.id)} style={{ cursor: 'pointer' }}>
                <td>{item.id}</td>
                <td>{renderListField(item.listaDfds)}</td>
                <td>{renderListField(item.listaItens)}</td>
                    
              </tr>
            ))
          ) : (
            <tr><td colSpan="4" className="text-center">Nenhum registro encontrado.</td></tr>
          )}
        </tbody>
      </TableContainer>
      </PageContent>
        <PageFooter>
          <Pagination10
            totalItems={totalItems}
            startIndex={paginationProps.startIndex}
            rowsPerPage={paginationProps.rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            onFirstItems={handleFirstItems}
            onPreviousItems={handlePreviousItems}
            onNextItems={handleNextItems}
            onLastItems={handleLastItems}
          />
        </PageFooter>

      <PcaConsolidadoModal show={showModal} onClose={() => setShowModal(false)} item={editingItem} onSave={handleSave} />
      <PcaConsolidationPickerModal show={showPicker} onClose={() => setShowPicker(false)} onConfirm={handlePerformConsolidation} showDefaultModal={showDefaultModal} consolidadoId={selectedId} />
      <PcaItensConsolidadosModal isOpen={showItensModal} onClose={() => setShowItensModal(false)} id={selectedId} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
      </ResponsiveShadow>
    </Layout>
  );
}

export default PcaConsolidado;
